/**
 * freeAlbum album document model — THE shared contract between editor, worker (PDF), layouts and tests.
 *
 * Units & coordinate system
 * - All geometry is in millimetres (mm) in the coordinate space of the SPREAD.
 *   Spread origin (0,0) = top-left of the LEFT page. Spread size = (2 × page.widthMm, page.heightMm).
 * - Cover strip coordinate space: [front][spine][back] for rtl binding (Hebrew), [back][spine][front] for ltr; width = 2 × page.widthMm + spineWidthMm.
 * - Font sizes are in points (pt). 1 pt = 0.3528 mm.
 * - Angles are degrees, clockwise, around the element's centre.
 * - Colors are CSS color strings (#rrggbb, #rrggbbaa, rgb(), hsl()). Never named colors.
 * - Every id is a nanoid (or any non-empty string). Ids are unique within the album document.
 *
 * Rendering guarantees
 * - The editor canvas and the PDF renderer use the SAME React components (@freealbum/render) fed by this model,
 *   so what the user sees is what Chromium prints.
 * - `Adjustments` are previewed with an SVG filter in the editor and BAKED into image derivatives at export;
 *   both sides are driven by the same tables in ./photo-pipeline, so the print matches the screen. The
 *   vignette is the one exception: it is an overlay the renderer draws in every mode.
 */
import { z } from 'zod';
import { CalendarOptions } from './calendar-options';
import { CUSTOM_SIZE_LIMITS, DEFAULT_BLEED_MM, EXPORT_DPI_LIMITS, type FormatId } from './formats';

export const DOC_VERSION = 1 as const;

// ---------- primitives ----------
export const Id = z.string().min(1).max(64);
export const Color = z.string().min(1).max(64);
export const Mm = z.number().finite();

export const FormatIdSchema = z.enum([
  'sq15', 'sq20', 'sq25', 'sq30', 'a4p', 'a4l', 'r20x30', 'r30x20', 'r25x35', 'r40x30',
  'in85sq', 'in10sq', 'in12sq', 'in8x10', 'in85x11', 'in11x85', 'in14x11',
  'custom',
]) satisfies z.ZodType<FormatId>;

export const PageSize = z.object({
  widthMm: Mm.min(CUSTOM_SIZE_LIMITS.minMm).max(CUSTOM_SIZE_LIMITS.maxMm),
  heightMm: Mm.min(CUSTOM_SIZE_LIMITS.minMm).max(CUSTOM_SIZE_LIMITS.maxMm),
});
export type PageSize = z.infer<typeof PageSize>;

/** Axis-aligned box + rotation about its centre. */
export const Frame = z.object({
  xMm: Mm,
  yMm: Mm,
  wMm: Mm.positive(),
  hMm: Mm.positive(),
  rotationDeg: z.number().finite().default(0),
});
export type Frame = z.infer<typeof Frame>;

// ---------- photo ----------
/** How the image is placed inside its frame (crop / zoom / pan). scale=1 means "cover" fit. */
export const ImageFit = z.object({
  scale: z.number().min(1).max(8).default(1),
  /** pan offsets as fraction of the overflow, -1..1, 0 = centred */
  offsetX: z.number().min(-1).max(1).default(0),
  offsetY: z.number().min(-1).max(1).default(0),
  /** image rotation in 90° steps, independent of frame rotation */
  rotate90: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
  /**
   * Fine rotation for levelling a horizon, degrees clockwise, on top of `rotate90`. The picture is
   * scaled up just enough that the frame stays covered at every angle (see ./image-fit).
   */
  straightenDeg: z.number().min(-45).max(45).default(0),
});
export type ImageFit = z.infer<typeof ImageFit>;

/**
 * Photo adjustments. Neutral values: brightness 1, contrast 1, saturation 1, everything else 0.
 * The pixel order is fixed by ./photo-pipeline: white balance → tone → colour → clarity → sharpen,
 * with the vignette drawn over the result.
 */
export const Adjustments = z.object({
  // ── light ──
  brightness: z.number().min(0.5).max(1.5).default(1),
  contrast: z.number().min(0.5).max(1.5).default(1),
  /** bends the upper half of the tone curve: + brightens highlights, − recovers them */
  highlights: z.number().min(-1).max(1).default(0),
  /** bends the lower half of the tone curve: + lifts shadows, − deepens them */
  shadows: z.number().min(-1).max(1).default(0),
  /** moves the white point: + pushes bright tones to paper white, − pulls them back */
  whites: z.number().min(-1).max(1).default(0),
  /** moves the black point: + lifts the blacks (matte), − crushes them */
  blacks: z.number().min(-1).max(1).default(0),
  // ── colour ──
  /** white balance, blue (−) ↔ amber (+) */
  temperature: z.number().min(-1).max(1).default(0),
  /** white balance, green (−) ↔ magenta (+) */
  tint: z.number().min(-1).max(1).default(0),
  saturation: z.number().min(0).max(2).default(1),
  grayscale: z.number().min(0).max(1).default(0),
  sepia: z.number().min(0).max(1).default(0),
  // ── detail ──
  /** local contrast (a wide-radius unsharp mask), 0..1 */
  clarity: z.number().min(0).max(1).default(0),
  /** edge sharpening (a narrow unsharp mask), 0..1; the radius follows the print resolution */
  sharpen: z.number().min(0).max(1).default(0),
  // ── effects ──
  /** darkened corners, 0..1; an overlay in the editor and in the print alike, never baked */
  vignette: z.number().min(0).max(1).default(0),
  /** id of a named preset from @freealbum/layouts ADJUSTMENT_PRESETS; null = custom/none */
  preset: z.string().max(64).nullable().default(null),
});
export type Adjustments = z.infer<typeof Adjustments>;

export const ShadowStyle = z.enum(['none', 'soft', 'medium', 'hard']);

export const PhotoStyle = z.object({
  borderWidthMm: Mm.min(0).max(20).default(0),
  borderColor: Color.default('#ffffff'),
  cornerRadiusMm: Mm.min(0).max(100).default(0),
  shadow: ShadowStyle.default('none'),
  opacity: z.number().min(0).max(1).default(1),
});
export type PhotoStyle = z.infer<typeof PhotoStyle>;

// ---------- text ----------
export const TextDirection = z.enum(['auto', 'rtl', 'ltr']);
export const TextAlign = z.enum(['start', 'center', 'end', 'justify']);

export const TextStyle = z.object({
  /** CSS font-family name as registered in the font registry (bundled or user font) */
  fontFamily: z.string().min(1).max(128).default('Frank Ruhl Libre'),
  fontSizePt: z.number().min(4).max(400).default(14),
  fontWeight: z.union([z.literal(300), z.literal(400), z.literal(500), z.literal(600), z.literal(700), z.literal(800), z.literal(900)]).default(400),
  italic: z.boolean().default(false),
  color: Color.default('#1b1b1f'),
  align: TextAlign.default('start'),
  direction: TextDirection.default('auto'),
  lineHeight: z.number().min(0.7).max(3).default(1.35),
  /** in em */
  letterSpacing: z.number().min(-0.2).max(1).default(0),
  backgroundColor: Color.nullable().default(null),
  paddingMm: Mm.min(0).max(50).default(0),
  /** vertical alignment of the paragraph block inside the frame */
  verticalAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
});
export type TextStyle = z.infer<typeof TextStyle>;

/** Plain-text paragraphs. Inline styling is intentionally out of scope for v1; one style per box. */
export const TextContent = z.object({
  paragraphs: z.array(z.string().max(5000)).min(1).default(['']),
});
export type TextContent = z.infer<typeof TextContent>;

// ---------- elements ----------
const ElementBase = z.object({
  id: Id,
  frame: Frame,
  /** z-order: higher renders on top. Renderer sorts by z then array order. */
  z: z.number().int().default(0),
  locked: z.boolean().default(false),
  hidden: z.boolean().default(false),
});

export const CaptionPlacement = z.enum(['below', 'above', 'overlay-bottom', 'overlay-top']);

export const PhotoElement = ElementBase.extend({
  type: z.literal('photo'),
  /** null = empty slot (placeholder rendered in editor, nothing printed) */
  photoId: Id.nullable().default(null),
  fit: ImageFit.default(() => ImageFit.parse({})),
  adjustments: Adjustments.default(() => Adjustments.parse({})),
  style: PhotoStyle.default(() => PhotoStyle.parse({})),
  caption: z
    .object({
      content: TextContent,
      style: TextStyle,
      placement: CaptionPlacement.default('below'),
      /** caption band height in mm (for below/above) */
      heightMm: Mm.min(3).max(60).default(8),
    })
    .nullable()
    .default(null),
  /** slot id from the layout this element was created from; kept after free move so "re-snap" works */
  slotId: z.string().max(64).nullable().default(null),
});
export type PhotoElement = z.infer<typeof PhotoElement>;

export const TextElement = ElementBase.extend({
  type: z.literal('text'),
  kind: z.enum(['text', 'title', 'caption', 'spine']).default('text'),
  content: TextContent,
  style: TextStyle.default(() => TextStyle.parse({})),
  slotId: z.string().max(64).nullable().default(null),
});
export type TextElement = z.infer<typeof TextElement>;

export const StickerElement = ElementBase.extend({
  type: z.literal('sticker'),
  /** asset id from the sticker catalog (packages/layouts/assets) */
  assetId: Id,
  tint: Color.nullable().default(null),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
  opacity: z.number().min(0).max(1).default(1),
});
export type StickerElement = z.infer<typeof StickerElement>;

export const ShapeElement = ElementBase.extend({
  type: z.literal('shape'),
  shape: z.enum(['rect', 'ellipse', 'line']),
  fill: Color.nullable().default('#1b1b1f'),
  stroke: Color.nullable().default(null),
  strokeWidthMm: Mm.min(0).max(20).default(0),
  cornerRadiusMm: Mm.min(0).max(100).default(0),
  opacity: z.number().min(0).max(1).default(1),
});
export type ShapeElement = z.infer<typeof ShapeElement>;

export const Element = z.discriminatedUnion('type', [PhotoElement, TextElement, StickerElement, ShapeElement]);
export type Element = z.infer<typeof Element>;
export type ElementType = Element['type'];

// ---------- background ----------
export const Background = z.discriminatedUnion('type', [
  z.object({ type: z.literal('color'), color: Color }),
  z.object({
    type: z.literal('gradient'),
    from: Color,
    to: Color,
    angleDeg: z.number().finite().default(180),
  }),
  z.object({
    type: z.literal('asset'),
    /** background asset id from the catalog (patterns/textures) */
    assetId: Id,
    tint: Color.nullable().default(null),
    opacity: z.number().min(0).max(1).default(1),
    /** tile = repeat pattern at its native size; cover = stretch to page */
    mode: z.enum(['tile', 'cover']).default('cover'),
  }),
  z.object({
    type: z.literal('photo'),
    /** a user photo used as full-bleed background */
    photoId: Id,
    fit: ImageFit.default(() => ImageFit.parse({})),
    adjustments: Adjustments.default(() => Adjustments.parse({})),
    opacity: z.number().min(0).max(1).default(1),
  }),
]);
export type Background = z.infer<typeof Background>;

// ---------- spreads ----------
export const Spread = z.object({
  id: Id,
  /** id of the layout template last applied (informational; elements are already materialised) */
  layoutId: z.string().max(64).nullable().default(null),
  /** if set, only that page physically exists (first page of the book is a lone right page, last a lone left page) */
  single: z.enum(['left', 'right']).nullable().default(null),
  background: Background.default({ type: 'color', color: '#ffffff' }),
  /** when set, the right page gets its own background; otherwise `background` spans the whole spread */
  backgroundRight: Background.nullable().default(null),
  elements: z.array(Element).default([]),
  /** free-form per-spread notes (not printed) */
  note: z.string().max(2000).nullable().default(null),
  /**
   * Labels shared with the photos meant for this spread ("pool time"). The tray narrows to them and
   * "fill from tags" lays them out here; nothing is printed. Normalised by `normalizeTags`.
   */
  tags: z.array(z.string().min(1).max(40)).max(20).default([]),
});
export type Spread = z.infer<typeof Spread>;

/**
 * Cover canvas — one strip in mm. Panel order depends on `binding`:
 * - 'rtl' (Hebrew, opens from the right): [front][spine][back]
 * - 'ltr' (opens from the left):          [back][spine][front]
 * createAlbumDocument derives binding from meta.lang; changing lang later does NOT flip it (explicit setting).
 */
export const Cover = z.object({
  enabled: z.boolean().default(true),
  binding: z.enum(['rtl', 'ltr']).default('rtl'),
  spineWidthMm: Mm.min(0).max(60).default(6),
  /**
   * 'auto': the spine follows the page count (`defaultSpineWidthMm`) and is recomputed whenever
   * pages are added or removed. 'manual': the user set `spineWidthMm` and it stays put.
   */
  spineWidthMode: z.enum(['auto', 'manual']).default('auto'),
  /**
   * Which way text on the spine reads when the book stands upright: 'down' (top to bottom, the
   * English-language convention) or 'up' (bottom to top, the Hebrew and continental convention).
   * null = follow the binding (rtl → 'up', ltr → 'down'); see `spineTextDirection()`.
   */
  spineTextDirection: z.enum(['down', 'up']).nullable().default(null),
  /** spine text is a normal text element with kind 'spine' inside `elements`, rotated by its frame */
  background: Background.default({ type: 'color', color: '#1b1b1f' }),
  /** painted over `background` on the spine band only; null = the spine shares the cover background */
  spineBackground: Background.nullable().default(null),
  /** id of the layout template last applied to the cover (informational) */
  layoutId: z.string().max(64).nullable().default(null),
  elements: z.array(Element).default([]),
});
export type Cover = z.infer<typeof Cover>;

// ---------- album ----------
export const AlbumMeta = z.object({
  title: z.string().max(200).default(''),
  subtitle: z.string().max(200).default(''),
  /** ISO 639-1 language of album content; affects default text direction & hyphenation */
  lang: z.enum(['he', 'en']).default('he'),
});

export const AlbumTheme = z.object({
  /** theme pack id from catalog: 'family' | 'wedding' | 'travel' | 'baby' | 'plain' | custom ids */
  id: z.string().max(64).default('family'),
  /** 5-colour palette; index 0 = page background default, 1 = text, 2..4 accents */
  palette: z.array(Color).min(3).max(8).default(['#ffffff', '#1b1b1f', '#c8553d', '#e0a030', '#2e5e4e']),
  headingFont: z.string().max(128).default('Frank Ruhl Libre'),
  bodyFont: z.string().max(128).default('David Libre'),
  /**
   * What the style paints on the cover and on every new page — and therefore what "restyle"
   * recognises as the style's own: a page still wearing it moves to the next style's, a page the
   * user painted does not. null on albums made before styles painted anything (their white pages
   * belong to nobody) and on a custom theme that leaves the schema defaults alone.
   */
  coverBackground: Background.nullable().default(null),
  pageBackground: Background.nullable().default(null),
});
export type AlbumTheme = z.infer<typeof AlbumTheme>;

/**
 * Where the album is going to be printed, chosen once so the editor can show that shop's safe zone
 * and cover wrap while the pages are being made, and so the export opens on the right profile.
 * `none` = a general PDF. Binding decides the cover wrap and the spine width.
 */
export const PrintTarget = z.object({
  service: z.enum(['none', 'prestophoto', 'blurb', 'lulu']).default('none'),
  binding: z.enum(['softcover', 'hardcover']).default('hardcover'),
});
export type PrintTarget = z.infer<typeof PrintTarget>;

export const AlbumDocument = z.object({
  version: z.literal(DOC_VERSION),
  /** Present for photo calendars. Generated dates remain ordinary editable elements. */
  calendar: CalendarOptions.optional(),
  format: FormatIdSchema,
  page: PageSize,
  bleedMm: Mm.min(0).max(10).default(DEFAULT_BLEED_MM),
  print: PrintTarget.default(() => PrintTarget.parse({})),
  /** first inner page stands alone on the right, last alone on the left */
  firstPageSingle: z.boolean().default(true),
  meta: AlbumMeta.default(() => AlbumMeta.parse({})),
  theme: AlbumTheme.default(() => AlbumTheme.parse({})),
  cover: Cover.default(() => Cover.parse({})),
  spreads: z.array(Spread).min(1),
});
export type AlbumDocument = z.infer<typeof AlbumDocument>;

// ---------- export options (stored on export_jobs.options) ----------
export const ExportOptions = z.object({
  dpi: z.number().int().min(EXPORT_DPI_LIMITS.min).max(EXPORT_DPI_LIMITS.max).default(EXPORT_DPI_LIMITS.default),
  jpegQuality: z.number().int().min(60).max(100).default(90),
  /** single = one PDF page per book page; spread = one PDF page per spread */
  pageMode: z.enum(['single', 'spread']).default('single'),
  /** separate = cover.pdf as a second file; bound = cover pages first/last inside album.pdf; none = no cover */
  cover: z.enum(['separate', 'bound', 'none']).default('separate'),
  /** adds bleedMm on every side and crop marks */
  printReady: z.boolean().default(false),
  /** also render flipbook page images (needed for share viewer) */
  flipbook: z.boolean().default(true),
  /**
   * Shape the file for one print-on-demand service (`PRINT_SERVICES`): its rules for page mode,
   * bleed, crop marks and cover placement override the fields above; dpi and quality stay yours.
   */
  printService: z.enum(['none', 'prestophoto', 'blurb', 'lulu']).default('none'),
});
export type ExportOptions = z.infer<typeof ExportOptions>;

// ---------- photo metadata as seen by the document consumers (from DB, not stored in doc) ----------
export const PhotoInfo = z.object({
  id: Id,
  name: z.string().nullable().optional(),
  bytes: z.number().nonnegative().optional(),
  mime: z.string().optional(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** EXIF DateTimeOriginal as ISO string, if any */
  takenAt: z.string().nullable(),
  status: z.enum(['uploading', 'processing', 'ready', 'failed', 'purged']),
  /** URLs resolved by the app for the current context (editor preview, export derivative, thumbnail) */
  urls: z.object({ thumb: z.string(), preview: z.string(), original: z.string().optional() }),
  /**
   * Where the interesting part of the picture is, 0..1 from the top left. Absent means "no
   * opinion", and a crop is centred exactly as it was before this existed.
   */
  focal: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).nullable().optional(),
  flags: z.object({ duplicateOf: Id.nullable().default(null), blurry: z.boolean().default(false) }).default(() => ({ duplicateOf: null, blurry: false })),
  contributorName: z.string().nullable().default(null),
  /** free-text labels, normalised by `normalizeTags`; the tray filters by them and spreads are filled from them */
  tags: z.array(z.string()).default([]),
  /** where the picture came from — the tray's "source" grouping for bulk tagging */
  source: z.enum(['upload', 'google-photos', 'google-drive', 'contribute']).default('upload'),
});
export type PhotoInfo = z.infer<typeof PhotoInfo>;
export type PhotoMap = Record<string, PhotoInfo>;
